<script lang="ts">
  import type { PutBridgeClientNotificationRequest } from '$lib/models/client/types';
  import { aliiNotificationCategoryStore } from '$lib/stores/aliiNotificationCategoryStore';
  import { type GetAliiNotificationCategoryResponse, NotificationType } from '$lib/models/alii-notification-category/types';
  import { onMount } from 'svelte';
  import NotificationRow from '$lib/components/drawers/client/edit/tabs/components/NotificationRow.svelte';

  export let notificationConfigs: PutBridgeClientNotificationRequest[];

  let notificationTypes: GetAliiNotificationCategoryResponse[];
  let loading: boolean = true;
  let expandedParents: Set<number> = new Set();

  function isVisibleNotification(notification: GetAliiNotificationCategoryResponse) {
    return notification.isVisibleInAdmin ?? true;
  }

  function toggleExpand(parentId: number) {
    if (expandedParents.has(parentId)) {
      const next = new Set(expandedParents);
      next.delete(parentId);
      expandedParents = next;
      return;
    }

    expandedParents = new Set([...expandedParents, parentId]);
  }

  function getParents(type: NotificationType) {
    return notificationTypes.filter((n) => n.type === type && n.parentCodeId == null && isVisibleNotification(n));
  }

  function getChildren(parentId: number) {
    return notificationTypes.filter((n) => n.parentCodeId === parentId && isVisibleNotification(n));
  }

  function getConfig(categoryId: number) {
    return notificationConfigs.find((nc) => nc.aliiNotificationCategoryId === categoryId);
  }

  onMount(async () => {
    await aliiNotificationCategoryStore
      .get()
      .then((result) => {
        notificationTypes = result;

        notificationTypes.map((notification) => {
          if (!isVisibleNotification(notification)) {
            return;
          }

          const existingCotificationConfig = notificationConfigs.find((nc) => nc.aliiNotificationCategoryId === notification.id);

          if (existingCotificationConfig) {
            return;
          }

          notificationConfigs = [
            ...notificationConfigs,
            {
              id: undefined,
              aliiNotificationCategoryId: notification.id,
              inApp: false,
              email: false,
              applyToAllUsers: false,
            },
          ];
        });
      })
      .finally(() => {
        loading = false;
      });
  });
</script>

<div>
  {#if loading}
    Loading...
  {:else}
    <div>
      <div>

        {#each [
          { label: 'Invoice Notifications', type: NotificationType.Invoice },
          { label: 'Purchase Order Notifications', type: NotificationType.PurchaseOrder },
          { label: 'Expense Claim Notifications', type: NotificationType.ExpenseClaim },
          { label: 'Credit Card Reconciliation Notifications', type: NotificationType.CreditCardRec },
          { label: 'Other Notifications', type: NotificationType.Other },
        ] as section}
          <div class="mb-4">
            <div class="grid grid-cols-10 gap-1">
              <div class="col-span-5 font-bold text-primary-500">{section.label}</div>
              <div class="col-span-2">Apply to All Users</div>
              <div>In App</div>
              <div>Email</div>
              {#each getParents(section.type) as parent}
                {@const parentConfig = getConfig(parent.id)}
                {@const children = getChildren(parent.id)}
                {@const isExpanded = expandedParents.has(parent.id)}
                {#if parentConfig}
                  <NotificationRow
                    notificationType={parent}
                    notificationConfig={parentConfig}
                    hasChildren={children.length > 0}
                    isExpanded={isExpanded}
                    toggleExpandAction={() => toggleExpand(parent.id)}
                  />
                {/if}
                {#if children.length > 0 && isExpanded}
                  {#each children as child}
                    {@const childConfig = getConfig(child.id)}
                    {#if childConfig}
                      <NotificationRow
                        notificationType={child}
                        notificationConfig={childConfig}
                        hasChildren={false}
                        isExpanded={false}
                        isChild={true}
                      />
                    {/if}
                  {/each}
                {/if}
              {/each}
            </div>
          </div>
        {/each}

      </div>
    </div>
  {/if}
</div>
